import * as Effects from 'redux-saga/effects';
import { JAVA_API_STATUS, SOMETHING_WRONG } from '../../utils/axios-interceptor';
import { toast } from 'react-toastify';
import * as apiService from './service';
import {
    fetchBankingPendingStatus,
    fetchBankingPendingStatusSuccess,
    fetchBankingPendingStatusFailure,
    triggerExtractPending,
    triggerExtractPendingSuccess,
    triggerExtractPendingFailure,
} from './bankingSlice';
import type { RootState } from '../store';

const call: any = Effects.call;
const put = Effects.put;
const select = Effects.select;
const takeLatest = Effects.takeLatest;
const delay = Effects.delay;

const POLL_INTERVAL_MS = 10000;
// 60 iterations × 10s = 10 min hard cap. Acts as a safety stop in case the
// stale-org guard or the in-flight check fails to break the loop.
const MAX_POLL_ITERATIONS = 60;

function buildStatusPayload(payload: any, orgId: string) {
    return {
        pendingCount: payload?.pendingCount ?? 0,
        inFlightCount: payload?.inFlightCount ?? 0,
        extractedCount: payload?.extractedCount ?? 0,
        showBankingTab: payload?.showBankingTab ?? true,
        orgId,
    };
}

// Polus UnifierResponse envelope: { responseBody: { ...counts, responseCode, responseMessage }, requestId }
// Fallback to legacy AicaResponse shape ({ responseData, data }) for forward compat.
function readEnvelope(response: any) {
    const body = response?.data?.responseBody ?? response?.data?.data ?? response?.data ?? {};
    const responseCode = body?.responseCode ?? response?.data?.responseData?.responseCode;
    const responseMessage = body?.responseMessage ?? response?.data?.responseData?.responseMessage;
    return { body, responseCode, responseMessage };
}

// Returns true if the org we started polling for is still the active one.
// Prevents stale-org writes when the user navigates between companies mid-poll.
function* isStillCurrentOrg(orgId: string) {
    const currentOrgId: string | undefined = yield select((state: RootState) => (state as any).investor?.selectedCompany?.assesseeOrgId);
    return !currentOrgId || currentOrgId === orgId;
}

function* fetchBankingPendingStatusSaga(action: ReturnType<typeof fetchBankingPendingStatus>) {
    try {
        const { orgId } = action.payload;
        const response = yield call(apiService.getBankingPendingStatus, orgId);
        const { body: payload, responseCode, responseMessage } = readEnvelope(response);

        switch (responseCode) {
            case JAVA_API_STATUS.SUCCESS: {
                yield put(fetchBankingPendingStatusSuccess(buildStatusPayload(payload, orgId)));
                // Cold-load polling: if backend reports in-flight work, kick off the poll loop.
                if ((payload?.inFlightCount ?? 0) > 0) {
                    yield call(pollBankingStatus, orgId);
                }
                break;
            }
            case JAVA_API_STATUS.ERROR:
            default:
                toast.error(responseMessage || SOMETHING_WRONG);
                yield put(fetchBankingPendingStatusFailure(responseMessage || SOMETHING_WRONG));
                break;
        }
    } catch (err: any) {
        yield put(fetchBankingPendingStatusFailure(err?.message || SOMETHING_WRONG));
        console.error('Error fetching banking pending status:', err?.message);
    }
}

function* triggerExtractPendingSaga(action: ReturnType<typeof triggerExtractPending>) {
    try {
        const { orgId } = action.payload;
        const pnoId: string = yield select((state: RootState) => state.user.userData.investorOrgId);
        if (!pnoId) {
            yield put(triggerExtractPendingFailure('User data not loaded'));
            return;
        }
        const response = yield call(apiService.triggerExtractPendingService, orgId, pnoId);
        const { body: payload, responseCode, responseMessage } = readEnvelope(response);

        switch (responseCode) {
            case JAVA_API_STATUS.SUCCESS: {
                const result = {
                    queuedCount: payload?.queuedCount ?? 0,
                    alreadyExtractedCount: payload?.alreadyExtractedCount ?? 0,
                    skippedCount: payload?.skippedCount ?? 0,
                };

                yield put(triggerExtractPendingSuccess(result));

                if (result.queuedCount > 0) {
                    toast.success(`Queued ${result.queuedCount} statements for extraction`);
                } else if (result.alreadyExtractedCount > 0 && result.queuedCount === 0) {
                    toast.info('All statements already extracted');
                } else if (result.skippedCount > 0 && result.queuedCount === 0) {
                    toast.info('Extraction already in progress');
                }

                // Refetch counts once. The fetch saga starts its own poll if
                // inFlightCount > 0, so we don't kick a second poll here — that
                // would race with the fetch's inner poll on shared slice state.
                yield put(fetchBankingPendingStatus({ orgId }));
                break;
            }
            case JAVA_API_STATUS.ERROR:
            default:
                toast.error(responseMessage || SOMETHING_WRONG);
                yield put(triggerExtractPendingFailure(responseMessage || SOMETHING_WRONG));
                break;
        }
    } catch (err: any) {
        yield put(triggerExtractPendingFailure(err?.message || SOMETHING_WRONG));
        console.error('Error triggering bank extract:', err?.message);
    }
}

// Polls banking status every POLL_INTERVAL_MS while any schedule is in-flight.
// Stops on: (a) inFlightCount drops to 0, (b) user navigates to a different
// company (stale-org guard), (c) error from the status endpoint, (d) safety cap.
function* pollBankingStatus(orgId: string) {
    for (let i = 0; i < MAX_POLL_ITERATIONS; i++) {
        yield delay(POLL_INTERVAL_MS);

        const stillCurrent: boolean = yield call(isStillCurrentOrg, orgId);
        if (!stillCurrent) return;

        try {
            const response = yield call(apiService.getBankingPendingStatus, orgId);
            const { body: payload, responseCode } = readEnvelope(response);
            if (responseCode === JAVA_API_STATUS.SUCCESS && payload) {
                // Re-check after the network call returned — org could have changed during it.
                const stillCurrentAfterFetch: boolean = yield call(isStillCurrentOrg, orgId);
                if (!stillCurrentAfterFetch) return;

                yield put(fetchBankingPendingStatusSuccess(buildStatusPayload(payload, orgId)));
                const inFlight = payload?.inFlightCount ?? 0;
                if (inFlight <= 0) return;
            }
        } catch (err) {
            console.warn('pollBankingStatus error, stopping:', err);
            return;
        }
    }
}

export default function* bankingSagaWatcher() {
    yield takeLatest(fetchBankingPendingStatus.type, fetchBankingPendingStatusSaga);
    yield takeLatest(triggerExtractPending.type, triggerExtractPendingSaga);
}
